import React, { useState } from 'react';
import AdminFilters from './AdminFilters';
import { EmptyState } from '../common';
import { formatDate, formatPrice, LISTING_TYPE_LABELS } from '../../utils/formatters';
import { SHIFT_STATUSES, DRIVER_POST_STATUSES, MEDALLION_POST_STATUSES } from '../../constants/admin';
import api from '../../services/api';

const AdminListingsTab = ({
  listingType,
  setListingType,
  shifts,
  driverPosts,
  medallionPosts,
  loading,
  filters,
  setFilters,
  onSearchShifts,
  onSearchDriverPosts,
  onSearchMedallionPosts,
  onEditShift,
  onDeleteShift,
  onEditDriverPost,
  onDeleteDriverPost,
  onEditMedallionPost,
  onDeleteMedallionPost,
  onBulkDeleteShifts,
  onBulkStatusChangeShifts,
  onBulkDeleteMedallionPosts,
  onBulkStatusChangeMedallionPosts
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');

  const isCar = listingType === 'car';
  const isDriver = listingType === 'driver';
  const isMedallion = listingType === 'medallion';
  const items = isCar ? shifts : isDriver ? driverPosts : (medallionPosts || []);
  const onSearch = isCar ? onSearchShifts : isDriver ? onSearchDriverPosts : onSearchMedallionPosts;
  const statuses = isCar ? SHIFT_STATUSES : isDriver ? DRIVER_POST_STATUSES : MEDALLION_POST_STATUSES;

  const handleToggle = (type) => {
    setListingType(type);
    setSelectedIds([]);
    setBulkStatus('');
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(items.map(item => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    if (isCar && onBulkDeleteShifts) {
      onBulkDeleteShifts(selectedIds);
    } else if (isMedallion && onBulkDeleteMedallionPosts) {
      onBulkDeleteMedallionPosts(selectedIds);
    }
    setSelectedIds([]);
  };

  const handleBulkStatusChange = () => {
    if (selectedIds.length === 0 || !bulkStatus) return;
    if (isCar && onBulkStatusChangeShifts) {
      onBulkStatusChangeShifts(selectedIds, bulkStatus);
    } else if (isMedallion && onBulkStatusChangeMedallionPosts) {
      onBulkStatusChangeMedallionPosts(selectedIds, bulkStatus);
    }
    setSelectedIds([]);
    setBulkStatus('');
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      let endpoint, filename;
      if (isCar) {
        endpoint = '/admin/shifts/export';
        filename = 'shifts_export.csv';
      } else if (isDriver) {
        endpoint = '/admin/driver-posts/export';
        filename = 'driver_posts_export.csv';
      } else {
        endpoint = '/admin/medallion-posts/export';
        filename = 'medallion_posts_export.csv';
      }

      const response = await api.get(`${endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const isAllSelected = items.length > 0 && selectedIds.length === items.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < items.length;

  const renderToggle = () => (
    <div className="listings-toggle">
      <button
        className={`listings-toggle-btn ${isCar ? 'active' : ''}`}
        onClick={() => handleToggle('car')}
      >
        Car Listings
      </button>
      <button
        className={`listings-toggle-btn ${isDriver ? 'active' : ''}`}
        onClick={() => handleToggle('driver')}
      >
        Driver Listings
      </button>
      <button
        className={`listings-toggle-btn ${isMedallion ? 'active' : ''}`}
        onClick={() => handleToggle('medallion')}
      >
        Medallion Listings
      </button>
    </div>
  );

  const renderBulkActionsBar = () => {
    if (isDriver || selectedIds.length === 0) return null;

    const bulkStatuses = isCar ? SHIFT_STATUSES : MEDALLION_POST_STATUSES;

    return (
      <div className="bulk-actions-bar">
        <span className="selected-count">
          {selectedIds.length} listing{selectedIds.length > 1 ? 's' : ''} selected
        </span>
        <div className="bulk-action-buttons">
          <select
            className="bulk-status-select"
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
          >
            <option value="">Change Status...</option>
            {bulkStatuses.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            className="btn-bulk-status"
            onClick={handleBulkStatusChange}
            disabled={!bulkStatus}
          >
            Apply
          </button>
          <button className="btn-bulk-delete" onClick={handleBulkDelete}>
            Delete Selected
          </button>
          <button className="btn-clear-selection" onClick={() => setSelectedIds([])}>
            Clear
          </button>
        </div>
      </div>
    );
  };

  const renderCarTable = () => (
    <div className="admin-table-wrapper">
      <table className="admin-table">
        <thead>
          <tr>
            <th className="checkbox-cell">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                onChange={handleSelectAll}
                title="Select all"
              />
            </th>
            <th>ID</th>
            <th>Title</th>
            <th>Owner</th>
            <th>Type</th>
            <th>Status</th>
            <th>Posted</th>
            <th>Pay</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {shifts.map(shift => (
            <tr key={shift.id} className={selectedIds.includes(shift.id) ? 'selected' : ''}>
              <td className="checkbox-cell">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(shift.id)}
                  onChange={() => handleSelectOne(shift.id)}
                />
              </td>
              <td>{shift.id}</td>
              <td>{shift.title}</td>
              <td>{shift.fleet_name || '-'}</td>
              <td>{shift.shift_type}</td>
              <td>
                <span className={`status-badge ${shift.effective_status || shift.status}`}>
                  {shift.effective_status || shift.status}
                </span>
              </td>
              <td>{formatDate(shift.created_at)}</td>
              <td>
                {shift.pay_rate
                  ? `$${shift.pay_rate}/${shift.pay_type === 'weekly' ? 'wk' : 'day'}`
                  : '-'}
              </td>
              <td className="actions">
                <button className="btn-edit" onClick={() => onEditShift(shift)}>Edit</button>
                <button className="btn-delete" onClick={() => onDeleteShift(shift.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderDriverTable = () => (
    <div className="admin-table-wrapper">
      <table className="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Title</th>
            <th>Driver</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {driverPosts.map(post => (
            <tr key={post.id}>
              <td>{post.id}</td>
              <td>{post.title}</td>
              <td>{post.driver_name}</td>
              <td>
                <span className={`status-badge ${post.effective_status || post.status}`}>
                  {post.effective_status || post.status}
                </span>
              </td>
              <td>{formatDate(post.created_at)}</td>
              <td className="actions">
                <button className="btn-edit" onClick={() => onEditDriverPost(post)}>Edit</button>
                <button className="btn-delete" onClick={() => onDeleteDriverPost(post.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderMedallionTable = () => (
    <div className="admin-table-wrapper">
      <table className="admin-table">
        <thead>
          <tr>
            <th className="checkbox-cell">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                onChange={handleSelectAll}
                title="Select all"
              />
            </th>
            <th>ID</th>
            <th>Title</th>
            <th>Type</th>
            <th>Price</th>
            <th>Qty</th>
            <th>Status</th>
            <th>Posted</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {(medallionPosts || []).map(post => (
            <tr key={post.id} className={selectedIds.includes(post.id) ? 'selected' : ''}>
              <td className="checkbox-cell">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(post.id)}
                  onChange={() => handleSelectOne(post.id)}
                />
              </td>
              <td>{post.id}</td>
              <td>{post.title}</td>
              <td>{LISTING_TYPE_LABELS[post.listing_type] || post.listing_type}</td>
              <td>{post.price ? formatPrice(post.price) : '-'}</td>
              <td>{post.quantity || 1}</td>
              <td>
                <span className={`status-badge ${post.effective_status || post.status}`}>
                  {post.effective_status || post.status}
                </span>
              </td>
              <td>{formatDate(post.created_at)}</td>
              <td className="actions">
                <button className="btn-edit" onClick={() => onEditMedallionPost(post)}>Edit</button>
                <button className="btn-delete" onClick={() => onDeleteMedallionPost(post.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return <div className="loading">Loading...</div>;
    }

    if (items.length === 0) {
      const emptyConfig = {
        car: { icon: '📅', title: 'No car listings found', empty: 'No car listings have been posted yet.' },
        driver: { icon: '🚗', title: 'No driver listings found', empty: 'No driver availability posts yet.' },
        medallion: { icon: '🏅', title: 'No medallion listings found', empty: 'No medallion listings have been posted yet.' }
      };
      const config = emptyConfig[listingType];
      return (
        <EmptyState
          icon={config.icon}
          title={config.title}
          message={filters.search || filters.status
            ? 'No listings match your current filters. Try adjusting your search criteria.'
            : config.empty}
        />
      );
    }

    if (isCar) return renderCarTable();
    if (isDriver) return renderDriverTable();
    return renderMedallionTable();
  };

  const searchPlaceholders = {
    car: 'Search by title or owner...',
    driver: 'Search by title or driver...',
    medallion: 'Search by title, poster, or medallion #...'
  };

  return (
    <>
      {renderToggle()}
      <div className="admin-actions-bar">
        <AdminFilters
          filters={filters}
          setFilters={setFilters}
          onSearch={onSearch}
          searchPlaceholder={searchPlaceholders[listingType]}
          filterOptions={statuses}
          filterKey="status"
          filterPlaceholder="All Statuses"
        />
        <button className="btn-export" onClick={handleExport}>
          Export CSV
        </button>
      </div>
      {renderBulkActionsBar()}
      {renderContent()}
    </>
  );
};

export default AdminListingsTab;
